import {useMutation, useQueryClient} from '@tanstack/react-query';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import campaignUpdaters from '@/api/updaters/campaign';

export type ToggleApplicationsAction = 'pause' | 'resume';

const ACTION_CONFIG: Record<
  ToggleApplicationsAction,
  {
    request: (campaignId: string) => Promise<unknown>;
    success: string;
    error: string;
  }
> = {
  pause: {
    request: (campaignId) => campaignUpdaters.closeProposalQueue(campaignId),
    success: 'Applications paused.',
    error: 'Failed to pause applications. Please try again.',
  },
  resume: {
    request: (campaignId) => campaignUpdaters.reopenProposalQueue(campaignId),
    success: 'Applications resumed.',
    error: 'Failed to resume applications. Please try again.',
  },
};

function useToggleCampaignApplications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      action,
    }: {
      campaignId: string;
      action: ToggleApplicationsAction;
    }) => ACTION_CONFIG[action].request(campaignId),

    onSuccess: (_data, {action}) => {
      raiseFlashMessage({
        status: 'success',
        message: translate(ACTION_CONFIG[action].success),
      });
    },

    onError: (_error, {action}) => {
      raiseFlashMessage({
        status: 'error',
        message: translate(ACTION_CONFIG[action].error),
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['campaignsV2List']});
    },
  });
}

export default useToggleCampaignApplications;
